import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, delay, map, tap } from 'rxjs/operators';
import { MicroServiceHttpService } from './microServiceHttp.service';
import type {
  WorkflowGraphResponseDto,
  WorkflowGraphSaveRequestDto,
} from '../models/chatbot-workflow-graph.model';

function withChatbotIdOnGraph(
  graph: WorkflowGraphResponseDto,
  chatbotId: number
): WorkflowGraphResponseDto {
  return {
    nodes: graph.nodes.map((n) => ({ ...n, chatbotId })),
    connectors: graph.connectors.map((c) => ({ ...c, chatbotId })),
    conditions: graph.conditions,
  };
}

function unwrapApiPayload<T>(raw: unknown): T | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('data' in o && o['data'] != null && typeof o['data'] === 'object') {
    return o['data'] as T;
  }
  return raw as T;
}

function isWorkflowGraphResponseDto(x: unknown): x is WorkflowGraphResponseDto {
  if (x == null || typeof x !== 'object') return false;
  const g = x as Record<string, unknown>;
  return Array.isArray(g['nodes']) && Array.isArray(g['connectors']);
}

function parseToolIds(body: Record<string, unknown>): number[] | undefined {
  const v = body['toolIds'];
  if (!Array.isArray(v)) return undefined;
  return v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
}

/** Detail payload merged into the node settings panel (GET …/workflow-node/:pid). */
export interface WorkflowNodeDetailsPatch {
  name?: string;
  systemPrompt?: string;
  overrideSystemPrompt?: boolean;
  modelId?: number;
  knowledgeBaseId?: number;
  llmConditionId?: number;
  expressionId?: number;
  toolIds?: number[];
}

@Injectable({ providedIn: 'root' })
export class ChatbotWorkflowService {
  private readonly microHttp = inject(MicroServiceHttpService);
  private cachedGraph: WorkflowGraphResponseDto | null = null;

  /**
   * GET full workflow graph: `GET whitelabel/chatbot/bot/:chatbotId/workflow`
   * → `{ nodes, connectors }` (nodes include `toolIds` when present).
   */
  getWorkflowGraph(chatbotId: number): Observable<WorkflowGraphResponseDto> {
    return this.microHttp
      .get<unknown>('whitelabel', `chatbot/bot/${chatbotId}/workflow`, {
        showToast: false,
        showLoader: false,
      })
      .pipe(
        map((raw) => {
          const body = unwrapApiPayload<unknown>(raw);
          if (!isWorkflowGraphResponseDto(body)) {
            throw new Error('Invalid workflow graph response shape');
          }
          return withChatbotIdOnGraph(body, chatbotId);
        }),
        tap((g) => {
          this.cachedGraph = g;
        })
      );
  }

  /**
   * GET node detail for the settings drawer: `GET …/workflow-node/:nodePid`
   * (falls back to cached graph row on failure).
   */
  getWorkflowNodeDetails(
    chatbotId: number,
    pid: number
  ): Observable<WorkflowNodeDetailsPatch> {
    return this.microHttp
      .get<unknown>('whitelabel', `chatbot/bot/${chatbotId}/workflow-node/${pid}`, {
        showToast: false,
        showLoader: false,
      })
      .pipe(
        map((raw) => {
          const body = unwrapApiPayload<Record<string, unknown>>(raw);
          if (!body || typeof body !== 'object') {
            throw new Error('Invalid node details response');
          }
          const toolIds = parseToolIds(body);
          return {
            name: typeof body['name'] === 'string' ? body['name'] : undefined,
            systemPrompt:
              typeof body['systemPrompt'] === 'string'
                ? body['systemPrompt']
                : body['systemPrompt'] === null
                  ? ''
                  : undefined,
            overrideSystemPrompt:
              typeof body['overrideSystemPrompt'] === 'boolean'
                ? body['overrideSystemPrompt']
                : undefined,
            modelId: typeof body['modelId'] === 'number' ? body['modelId'] : undefined,
            knowledgeBaseId:
              typeof body['knowledgeBaseId'] === 'number' ? body['knowledgeBaseId'] : undefined,
            llmConditionId:
              typeof body['llmConditionId'] === 'number' ? body['llmConditionId'] : undefined,
            expressionId:
              typeof body['expressionId'] === 'number' ? body['expressionId'] : undefined,
            ...(toolIds !== undefined ? { toolIds } : {}),
          } satisfies WorkflowNodeDetailsPatch;
        }),
        catchError(() => this.nodeDetailsFromCache(pid))
      );
  }

  private nodeDetailsFromCache(pid: number): Observable<WorkflowNodeDetailsPatch> {
    const base = this.cachedGraph;
    if (!base) {
      return of({
        name: 'Agent',
        systemPrompt: '',
        overrideSystemPrompt: false,
      }).pipe(delay(0));
    }
    const node = base.nodes.find((n) => n.pid === pid);
    if (!node) {
      return of({
        name: 'Agent',
        systemPrompt: '',
        overrideSystemPrompt: false,
      }).pipe(delay(0));
    }
    return of(node).pipe(
      map((n) => ({
        name: n.name,
        systemPrompt: n.systemPrompt ?? '',
        overrideSystemPrompt: n.overrideSystemPrompt,
        modelId: n.modelId ?? undefined,
        knowledgeBaseId: n.knowledgeBaseId ?? undefined,
        llmConditionId: n.llmConditionId ?? undefined,
        expressionId: n.expressionId ?? undefined,
        ...(n.toolIds !== undefined ? { toolIds: [...n.toolIds] } : {}),
      })),
      delay(0)
    );
  }

  /**
   * PUT full graph: `PUT whitelabel/chatbot/bot/:chatbotId/workflow`
   * Body: `{ nodes, connectors }` (both required; `connectors` may be `[]`).
   * Returns the saved graph for client refresh.
   */
  updateWorkflowGraph(
    chatbotId: number,
    payload: WorkflowGraphSaveRequestDto,
    options: { showLoader?: boolean; showToast?: boolean } = {}
  ): Observable<WorkflowGraphResponseDto> {
    return this.microHttp
      .put<unknown>('whitelabel', `chatbot/bot/${chatbotId}/workflow`, payload, {
        showToast: options?.showToast ?? false,
        showLoader: options?.showLoader ?? true,
        successMessage: 'Workflow saved',
      })
      .pipe(
        map((raw) => {
          const body = unwrapApiPayload<unknown>(raw);
          if (!isWorkflowGraphResponseDto(body)) {
            throw new Error('Invalid workflow graph save response');
          }
          return withChatbotIdOnGraph(body, chatbotId);
        }),
        tap((g) => {
          this.cachedGraph = g;
        })
      );
  }
}
